import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Tv, Loader2, Check, X, Plus, Pencil } from 'lucide-react'
import { useToast } from '../../contexts/ToastContext'
import broadcastAdminService, {
  ReferenceType, RefLeague, RefSport, RefVenue
} from '../../services/broadcastAdminService'
import { formatChannelType } from './customer/broadcastCustomerFormat'
import ReferenceEditDrawer, { FieldDef } from './ReferenceEditDrawer'

type TabId = ReferenceType

/** Tab order follows how often Support reaches for each. */
const TABS: { id: TabId; name: string; noun: string; canAdd: boolean }[] = [
  { id: 'channels', name: 'Feed providers', noun: 'feed provider', canAdd: true },
  { id: 'zones', name: 'Placements', noun: 'placement', canAdd: true },
  { id: 'leagues', name: 'Leagues', noun: 'league', canAdd: true },
  { id: 'teams', name: 'Teams', noun: 'team', canAdd: true },
  { id: 'venues', name: 'Venues', noun: 'venue', canAdd: true },
  { id: 'sports', name: 'Sports', noun: 'sport', canAdd: false },
  { id: 'viewership-sources', name: 'Viewership sources', noun: 'viewership source', canAdd: false },
  { id: 'competition-stages', name: 'Competition stages', noun: 'competition stage', canAdd: false }
]

const CHANNEL_TYPE_OPTIONS = ['linear', 'svod', 'fast', 'free_stream', 'social', 'other']
  .map((t) => ({ value: t, label: formatChannelType(t) ?? t }))

/** Placements are keyed by code; everything else by uuid. */
const rowKey = (row: any) => row.id ?? row.code

/**
 * Broadcast Admin — the platform-wide broadcast reference data.
 *
 * NOT reachable from an organization, and no customerId anywhere on this page.
 * Brisbane Bandits is the same team whoever is reporting on them, and a form
 * headed with a customer's name must not contain a control that disables a
 * league for everyone. The per-customer page is "Broadcasts — <org>".
 *
 * Nothing is ever deleted — is_active only. A deleted team with broadcasts
 * pointing at it is a broken report.
 */
const ReferenceDataPage: React.FC = () => {
  const navigate = useNavigate()
  const { showSuccess, showError } = useToast()
  const [activeTab, setActiveTab] = useState<TabId>('channels')
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showInactive, setShowInactive] = useState(false)
  // Lookups for the selects and the Sports column. Active rows only: a
  // disabled league is not something to file a new team under.
  const [sports, setSports] = useState<RefSport[]>([])
  const [leagues, setLeagues] = useState<RefLeague[]>([])
  const [venues, setVenues] = useState<RefVenue[]>([])
  const [editing, setEditing] = useState<{ row: any | null } | null>(null)

  const tab = TABS.find((t) => t.id === activeTab)!

  const loadLookups = useCallback(async () => {
    try {
      const [s, l, v] = await Promise.all([
        broadcastAdminService.listReference<RefSport>('sports'),
        broadcastAdminService.listReference<RefLeague>('leagues'),
        broadcastAdminService.listReference<RefVenue>('venues')
      ])
      setSports(s.data || []); setLeagues(l.data || []); setVenues(v.data || [])
    } catch { /* the table still loads; selects just come up short */ }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await broadcastAdminService.listReference<any>(activeTab, showInactive)
      setRows(res.data || [])
    } catch (err) {
      showError('Failed to load', err instanceof Error ? err.message : 'Please try again')
    } finally {
      setLoading(false)
    }
  }, [activeTab, showInactive, showError])

  useEffect(() => { void load() }, [load])
  useEffect(() => { void loadLookups() }, [loadLookups])

  const sportName = useMemo(() => new Map(sports.map((s) => [s.id, s.name])), [sports])
  const sportOptions = sports.map((s) => ({ value: s.id, label: s.name }))

  const fields: Record<TabId, FieldDef[]> = {
    channels: [
      { key: 'name', rowKey: 'name', label: 'Name', type: 'text', required: true, maxLength: 120, placeholder: 'e.g. ESPN+' },
      { key: 'code', rowKey: 'code', label: 'Code', type: 'text', required: true, createOnly: true, slugFrom: 'name', maxLength: 50 },
      { key: 'channelType', rowKey: 'channel_type', label: 'Type', type: 'select', required: true, options: CHANNEL_TYPE_OPTIONS },
      { key: 'territory', rowKey: 'territory', label: 'Territory', type: 'text', maxLength: 80, placeholder: 'e.g. Australia' },
      { key: 'displayOrder', rowKey: 'display_order', label: 'Display order', type: 'number', help: 'Lower numbers list first.' }
    ],
    zones: [
      { key: 'label', rowKey: 'label', label: 'Name', type: 'text', required: true, maxLength: 100, placeholder: 'e.g. Dugout roof' },
      { key: 'code', rowKey: 'code', label: 'Code', type: 'text', required: true, createOnly: true, slugFrom: 'label', maxLength: 50 },
      { key: 'description', rowKey: 'description', label: 'Description', type: 'textarea', required: true, maxLength: 500,
        placeholder: 'Where it is and how to tell it apart from nearby placements',
        help: 'The AI reads this to decide what belongs here, so be specific. Changes apply from the next analysis.' },
      { key: 'sportIds', rowKey: 'sport_ids', label: 'Sports', type: 'multi', options: sportOptions,
        help: 'Leave all unticked to offer it for every sport.' },
      { key: 'displayOrder', rowKey: 'display_order', label: 'Display order', type: 'number', help: 'Lower numbers list first.' }
    ],
    leagues: [
      { key: 'name', rowKey: 'name', label: 'Name', type: 'text', required: true, maxLength: 150, placeholder: 'e.g. Australian Baseball League' },
      { key: 'shortName', rowKey: 'short_name', label: 'Short name', type: 'text', maxLength: 50, placeholder: 'e.g. ABL' },
      { key: 'code', rowKey: 'code', label: 'Code', type: 'text', required: true, createOnly: true, slugFrom: 'name', maxLength: 50 },
      { key: 'kind', rowKey: 'kind', label: 'Kind', type: 'select', required: true,
        options: [{ value: 'league', label: 'League' }, { value: 'association', label: 'Association' }, { value: 'conference', label: 'Conference' }] },
      { key: 'sportId', rowKey: 'sport_id', label: 'Sport', type: 'select', options: sportOptions, emptyLabel: 'Several sports',
        help: 'Leave as "Several sports" for bodies like the NCAA; each team then names its sport.' },
      { key: 'country', rowKey: 'country', label: 'Country', type: 'text', maxLength: 2, upper: true, placeholder: 'AU' },
      { key: 'seasonFormat', rowKey: 'season_format', label: 'Season', type: 'select', required: true,
        options: [{ value: 'calendar', label: 'Calendar year (2026)' }, { value: 'split', label: 'Split across years (2026-27)' }] },
      { key: 'displayOrder', rowKey: 'display_order', label: 'Display order', type: 'number' }
    ],
    teams: [
      { key: 'leagueId', rowKey: 'league_id', label: 'League', type: 'select', required: true,
        options: leagues.map((l) => ({ value: l.id, label: l.short_name ? `${l.short_name} — ${l.name}` : l.name })) },
      { key: 'name', rowKey: 'name', label: 'Name', type: 'text', required: true, maxLength: 150, placeholder: 'e.g. Brisbane Bandits' },
      { key: 'shortName', rowKey: 'short_name', label: 'Short name', type: 'text', maxLength: 50, placeholder: 'e.g. Bandits' },
      { key: 'abbreviation', rowKey: 'abbreviation', label: 'Abbreviation', type: 'text', maxLength: 10, upper: true, placeholder: 'e.g. BRI' },
      { key: 'sportId', rowKey: 'sport_id', label: 'Sport', type: 'select', options: sportOptions, emptyLabel: "The league's sport",
        help: 'Only needed when the league covers several sports.' },
      { key: 'homeVenueId', rowKey: 'home_venue_id', label: 'Home venue', type: 'select', emptyLabel: 'None',
        options: venues.map((v) => ({ value: v.id, label: v.city ? `${v.name}, ${v.city}` : v.name })) }
    ],
    venues: [
      { key: 'name', rowKey: 'name', label: 'Name', type: 'text', required: true, maxLength: 200, placeholder: 'e.g. Holloway Field' },
      { key: 'city', rowKey: 'city', label: 'City', type: 'text', maxLength: 100 },
      { key: 'region', rowKey: 'region', label: 'Region', type: 'text', maxLength: 100, placeholder: 'State or province' },
      { key: 'country', rowKey: 'country', label: 'Country', type: 'text', maxLength: 2, upper: true, placeholder: 'AU' },
      { key: 'timezone', rowKey: 'timezone', label: 'Timezone', type: 'text', maxLength: 50, placeholder: 'e.g. Australia/Brisbane' }
    ],
    sports: [
      { key: 'name', rowKey: 'name', label: 'Name', type: 'text', required: true, maxLength: 100 },
      { key: 'displayOrder', rowKey: 'display_order', label: 'Display order', type: 'number' }
    ],
    'viewership-sources': [
      { key: 'name', rowKey: 'name', label: 'Name', type: 'text', required: true, maxLength: 100 },
      { key: 'isEstimate', rowKey: 'is_estimate', label: 'This source gives estimates, not measured figures', type: 'checkbox' },
      { key: 'displayOrder', rowKey: 'display_order', label: 'Display order', type: 'number', help: 'Most reliable first.' }
    ],
    'competition-stages': [
      { key: 'name', rowKey: 'name', label: 'Name', type: 'text', required: true, maxLength: 100 },
      { key: 'displayOrder', rowKey: 'display_order', label: 'Display order', type: 'number', help: 'Order through a season, pre-season first.' }
    ]
  }

  const save = async (values: Record<string, unknown>, isNew: boolean) => {
    const v = values as any
    const row = editing?.row
    if (activeTab === 'zones') {
      if (isNew) await broadcastAdminService.createZone(v)
      else await broadcastAdminService.updateZone(row.code, v)
    } else if (isNew) {
      if (activeTab === 'channels') await broadcastAdminService.createChannel(v)
      else if (activeTab === 'leagues') await broadcastAdminService.createLeague(v)
      else if (activeTab === 'teams') await broadcastAdminService.createTeam(v)
      else if (activeTab === 'venues') await broadcastAdminService.createVenue(v)
    } else {
      await broadcastAdminService.updateReference(activeTab, row.id, v)
    }
    showSuccess(isNew ? `${tab.noun[0].toUpperCase()}${tab.noun.slice(1)} added` : 'Saved',
      String(v.name ?? v.label ?? row?.name ?? row?.label ?? ''))
    await load()
    if (activeTab === 'leagues' || activeTab === 'venues') await loadLookups()
  }

  const toggleActive = async (row: any) => {
    const name = row.name ?? row.label
    try {
      if (activeTab === 'zones') await broadcastAdminService.updateZone(row.code, { isActive: !row.is_active })
      else await broadcastAdminService.updateReference(activeTab, row.id, { isActive: !row.is_active })
      showSuccess(row.is_active ? 'Disabled' : 'Enabled', name)
      await load()
    } catch (err) {
      showError('Could not update', err instanceof Error ? err.message : 'Please try again')
    }
  }

  const columns: Record<TabId, { key: string; label: string }[]> = {
    channels: [
      { key: 'name', label: 'Name' }, { key: 'channel_type', label: 'Type' },
      { key: 'territory', label: 'Territory' }, { key: 'code', label: 'Code' }
    ],
    zones: [
      { key: 'label', label: 'Name' }, { key: 'sport_ids', label: 'Sports' },
      { key: 'description', label: 'Description' }, { key: 'code', label: 'Code' }
    ],
    sports: [{ key: 'name', label: 'Name' }, { key: 'code', label: 'Code' }],
    leagues: [
      { key: 'short_name', label: 'Short' }, { key: 'name', label: 'Name' },
      { key: 'kind', label: 'Kind' }, { key: 'sport_code', label: 'Sport' },
      { key: 'season_format', label: 'Season' }, { key: 'country', label: 'Country' }
    ],
    teams: [
      { key: 'abbreviation', label: 'Abbr' }, { key: 'name', label: 'Name' },
      { key: 'league_short_name', label: 'League' }, { key: 'sport_code', label: 'Sport' },
      { key: 'home_venue_name', label: 'Home venue' }
    ],
    venues: [
      { key: 'name', label: 'Name' }, { key: 'city', label: 'City' },
      { key: 'region', label: 'Region' }, { key: 'country', label: 'Country' },
      { key: 'timezone', label: 'Timezone' }
    ],
    'viewership-sources': [
      { key: 'name', label: 'Name' }, { key: 'code', label: 'Code' },
      { key: 'is_estimate', label: 'Quality' }
    ],
    'competition-stages': [
      { key: 'name', label: 'Name' }, { key: 'code', label: 'Code' }
    ]
  }

  const render = (row: any, key: string) => {
    if (key === 'is_estimate') {
      return (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${row.is_estimate ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
          {row.is_estimate ? 'Estimate' : 'Measured'}
        </span>
      )
    }
    if (key === 'channel_type') return formatChannelType(row.channel_type)
    if (key === 'sport_ids') {
      const ids: string[] = row.sport_ids ?? []
      return ids.length === 0
        ? <span className="text-gray-400">All sports</span>
        : ids.map((id) => sportName.get(id) ?? '?').join(', ')
    }
    if (key === 'description') {
      return <span className="block max-w-md truncate text-gray-500" title={row.description ?? ''}>{row.description}</span>
    }
    if (key === 'code') return <span className="font-mono text-xs text-gray-500">{row.code}</span>
    return row[key] ?? <span className="text-gray-300">—</span>
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate('/support')}
          className="inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-800 mb-3 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Support
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Tv className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Broadcast Admin</h1>
            <p className="text-xs text-gray-500">Platform-wide. Changes here affect every customer.</p>
          </div>
        </div>

        <div className="bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 mb-5">
          <p className="text-xs text-violet-900">
            These lists are shared across the platform — the same feed provider, placement, team and
            venue appear for every customer. Nothing is deleted here; disable instead, because a
            removed entry with broadcasts pointing at it is a broken report.
          </p>
        </div>

        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex space-x-1 bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg shadow-inner overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 rounded-md text-xs font-semibold transition-all whitespace-nowrap ${
                  activeTab === t.id
                    ? 'bg-white text-violet-700 shadow-md border border-white/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                {t.name}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
                     className="rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
              Show disabled
            </label>
            {tab.canAdd && (
              <button onClick={() => setEditing({ row: null })}
                      className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 shadow-sm">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add {tab.noun}
              </button>
            )}
          </div>
        </div>

        {activeTab === 'zones' && (
          <p className="text-xs text-gray-500 mb-3">
            Placements are where in the venue a brand appears. The AI is given this list, filtered to
            the broadcast's sport, on every analysis, so edits apply from the next one.
          </p>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left">
                  {columns[activeTab].map((c) => (
                    <th key={c.key} className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">{c.label}</th>
                  ))}
                  <th className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">Status</th>
                  <th className="px-2 py-2 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length === 0 && (
                  <tr><td colSpan={columns[activeTab].length + 2} className="px-4 py-8 text-center text-gray-400 text-xs">Nothing here yet.</td></tr>
                )}
                {rows.map((row) => {
                  // The fallback for anything the AI cannot place; disabling it would fail runs.
                  const fixed = activeTab === 'zones' && row.code === 'unknown'
                  return (
                    <tr key={rowKey(row)} className={`hover:bg-gray-50 ${row.is_active === false ? 'opacity-50' : ''}`}>
                      {columns[activeTab].map((c) => (
                        <td key={c.key} className="px-4 py-2 text-gray-700">{render(row, c.key)}</td>
                      ))}
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => toggleActive(row)}
                          disabled={fixed}
                          className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-colors disabled:cursor-not-allowed ${
                            row.is_active !== false
                              ? 'text-emerald-700 hover:bg-emerald-50 disabled:hover:bg-transparent'
                              : 'text-gray-500 hover:bg-gray-100'
                          }`}
                          title={fixed ? 'Always on: the fallback for placements the AI cannot name' : row.is_active !== false ? 'Disable' : 'Enable'}
                        >
                          {row.is_active !== false ? <><Check className="h-3.5 w-3.5 mr-1" />Active</> : <><X className="h-3.5 w-3.5 mr-1" />Disabled</>}
                        </button>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button onClick={() => setEditing({ row })} title="Edit"
                                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ReferenceEditDrawer
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        noun={tab.noun}
        fields={fields[activeTab]}
        row={editing?.row ?? null}
        onSave={save}
      />
    </div>
  )
}

export default ReferenceDataPage
